import CardHeader from '@packages/ui/card/card-header.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import useFormData from '@client/module/utils/use-form-data.ts';
import FormItem from '@packages/ui/form/form-item.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import Card from '@packages/ui/card/card.tsx';
import { useCallback, useState } from 'react';
import { useTableCreateBatch } from '@client/page/management/table/table-create-batch/logic/use-table-create-batch.ts';
import { useTranslate } from '@tolgee/react';
import { useNotificationCenter } from 'ui/src/notification/notification-center';

interface FormData {
  pattern: string;
  start: string;
  end: string;
  seats: string;
}

interface ValidationStatus {
  status: boolean;
  error: Partial<FormData>;
}

function validateTableCreateBatch(
  data: FormData,
  t: (translateString: string, defaultString: string) => string
): ValidationStatus {
  let status = true;
  const error: Partial<FormData> = {};

  if (!data.pattern) {
    status = false;
    error.pattern = t('table-create-batch.error.pattern_not_empty', 'Table prefix is not empty');
  }

  if (parseInt(data.seats) <= 0) {
    status = false;
    error.seats = t('table-create-batch.error.seats_greater_than_0', 'Seats must be greater than 0');
  }

  if (!data.seats) {
    status = false;
    error.seats = t('table-create-batch.error.seats_not_empty', 'Seats is not empty');
  }

  if (parseInt(data.seats) > 99) {
    status = false;
    error.seats = t('table-create-batch.error.seats_less_than_100', 'Seats must be less than 100');
  }

  if (!data.start) {
    status = false;
    error.start = t('table-create-batch.error.start_not_empty', 'Start is not empty');
  }

  if (parseInt(data.start) < 0) {
    status = false;
    error.start = t('table-create-batch.error.start_greater_equal_0', 'Start must be greater than or equal to 0');
  }

  if (!data.end) {
    status = false;
    error.end = t('table-create-batch.error.end-not-empty', 'End is not empty');
  }

  if (parseInt(data.end) <= 0) {
    status = false;
    error.end = t('table-create-batch.error.end_greater_than_0', 'End must be greater than 0');
  }

  if (parseInt(data.end) <= parseInt(data.start)) {
    status = false;
    error.end = t('table-create-batch.error.end_greater_than_start', 'End must be greater than start');
  }
  return { status: status, error: error };
}

export default function TableCreateBatch(props: { tableGroupId: string; onClose?: () => void }) {
  const [create] = useTableCreateBatch();
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  const { data, handle } = useFormData<FormData>({
    pattern: '',
    start: '1',
    end: '2',
    seats: '1',
  });

  const [validation, setValidation] = useState<ValidationStatus>({
    status: true,
    error: {},
  });

  const generateTableList = useCallback(() => {
    const batch = [];
    for (let i = parseInt(data.start); i <= parseInt(data.end); i++) {
      batch.push({
        name: `${data.pattern}${i.toString()}`,
        seats: parseInt(data.seats),
      });
    }

    return batch;
  }, [data]);

  const submit = useCallback(async () => {
    const updatedValidation = validateTableCreateBatch(data, (translateString, defaultString) =>
      t(translateString, defaultString)
    );

    if (!updatedValidation.status) {
      setValidation(updatedValidation);
      return;
    }

    const batch = generateTableList();

    try {
      await create({
        variables: {
          tableGroup: props.tableGroupId,
          data: batch,
        },
      });
      props.onClose?.();
      showAlert({
        status: 'success',
        title: t('common.alert.success'),
        message: t('create.batch-table.success'),
      });
    } catch (e) {
      /* eslint-disable no-console */
      console.error(e);
    }
  }, [create, generateTableList, props, showAlert, t, data]);

  const generateRangeSeatErrorText = () => {
    if (!data.end || !data.start) {
      return t('table-batch.preview.error', 'Please fill in the start and end information completely.');
    }

    return '';
  };

  return (
    <Card>
      <CardHeader title={t('table-create-batch.header')} withBackButton={true} backTarget={-1} />
      <CardContent>
        <FormLayout>
          <FormItem title={t('table-create-batch.form.pattern_title', 'Pattern')} className="max-w-sm">
            <TextInput
              type="text"
              label="pattern"
              value={data.pattern}
              name="pattern"
              placeholder={t('table-create-batch.form.pattern_placeholder', 'Example: A')}
              error={validation.error.pattern}
              onChange={handle}
            />
          </FormItem>

          <FormItem className="max-w-sm" title={t('table-create-batch.form.seats_title', 'Seats')}>
            <TextInput
              type="number"
              label="seats"
              value={data.seats}
              name="seats"
              placeholder={t('table-create-batch.form.seats_placeholder', 'Enter number of seats')}
              error={validation.error.seats}
              onChange={handle}
            />
          </FormItem>

          <div className="flex space-x-4 max-w-sm items-center">
            <FormItem className="min-h-28" title={t('table-create-batch.form.start_title', 'Start')}>
              <TextInput
                type="number"
                label="start"
                value={data.start}
                name="start"
                placeholder={t('table-create-batch.form.start_placeholder', 'Enter integer')}
                error={validation.error.start}
                onChange={handle}
              />
            </FormItem>

            <FormItem className="min-h-28" title={t('table-create-batch.form.end_title', 'End')}>
              <TextInput
                type="number"
                label="end"
                value={data.end}
                name="end"
                placeholder={t('table-create-batch.form.end_placeholder', 'Enter integer')}
                error={validation.error.end}
                onChange={handle}
              />
            </FormItem>
          </div>

          {data.pattern && data.end && data.start && data.end > data.start ? (
            <FormItem className="-mt-4" title={t('table-create-batch.form.preview_title', 'Preview')}>
              <p>
                <span className="font-semibold">
                  {(() => {
                    const tableList = generateTableList().map((table) => table.name);
                    if (tableList.length <= 2) {
                      return tableList.join(', ');
                    }
                    return `${tableList[0] ?? ''}, ..., ${tableList[tableList.length - 1] ?? ''}`;
                  })()}
                </span>
                <span> {t('table-batch.profix', 'will be created')}</span>
              </p>
            </FormItem>
          ) : (
            <FormItem title="">
              <p className="text-red-600 -mt-4">{generateRangeSeatErrorText()}</p>
            </FormItem>
          )}
        </FormLayout>
      </CardContent>

      <CardFooter>
        <PrimaryButton
          className="w-32"
          onClick={() => {
            submit().catch((e: unknown) => void e);
          }}
        >
          {t('table-create-batch.button.run', 'Run')}
        </PrimaryButton>
        <SecondaryButton className="w-32" onClick={props.onClose}>
          {t('table-create-batch.button.cancel', 'Cancel')}
        </SecondaryButton>
      </CardFooter>
    </Card>
  );
}
